import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { X, User, Building, Mail, Phone, Globe, Save, Plus, Package, Languages, Wand2, ClipboardPaste, Loader2, AlertTriangle } from 'lucide-react';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { Lead, Company, Product } from '../../types';
import { Button, IconButton } from '../shared';
import { companiesService } from '../../services/companiesService';
import { usersService } from '../../services/usersService';
import { openaiService } from '../../services/openaiService';
import { LANGUAGES } from '../../utils/languageHelpers';
import { useCustomLeadSources } from '../../hooks/useCustomLeadSources';

interface AddLeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddLead: (leadData: Omit<Lead, 'id' | 'createdAt' | 'updatedAt' | 'notes' | 'activities'>) => void;
}

type LeadFormData = {
  title: string;
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  phone: string;
  phoneSecondary: string;
  language: Lead['language'];
  source: Lead['source'];
};

const COMMON_JOB_WORDS = [
  'manager', 'director', 'geschäftsführer', 'geschaeftsfuehrer', 'leitung', 'sales',
  'marketing', 'consultant', 'berater', 'founder', 'owner', 'ceo', 'cfo', 'cto',
  'account', 'assistant', 'team', 'department', 'abteilung', 'vertrieb'
];

function cleanExtractedValue(value: unknown): string {
  return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';
}

function parseNameLine(line: string): Pick<LeadFormData, 'title' | 'firstName' | 'lastName'> | null {
  const cleaned = line.replace(/^(name|kontakt|contact)\s*:\s*/i, '').trim();
  if (!cleaned || /[@\d]|www\.|https?:\/\//i.test(cleaned)) return null;
  if (COMMON_JOB_WORDS.some(word => cleaned.toLowerCase().includes(word))) return null;
  if (/\b(gmbh|ag|kg|ug|inc|llc|ltd|sarl|bv|company|group)\b/i.test(cleaned)) return null;

  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length < 2 || parts.length > 5) return null;

  const titleParts: string[] = [];
  while (parts.length > 0 && /^(dr\.?|prof\.?|mr\.?|mrs\.?|ms\.?|herr|frau)$/i.test(parts[0])) {
    titleParts.push(parts.shift() || '');
  }
  if (parts.length < 2) return null;

  return {
    title: titleParts.join(' '),
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  };
}

function extractLeadLocally(signatureText: string): Partial<LeadFormData> {
  const text = signatureText.replace(/\r/g, '\n');
  const lines = text
    .split('\n')
    .map(line => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean);

  const email = text.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i)?.[0] || '';
  const phoneMatches = Array.from(text.matchAll(/(?:\+|00)?[\d][\d\s()./-]{6,}\d/g))
    .map(match => match[0].replace(/^(tel|phone|mobile|mobil|fon|fax)\s*:\s*/i, '').trim())
    .filter(value => value.replace(/\D/g, '').length >= 7);

  const companyLine = lines.find(line => /\b(gmbh|ag|kg|ug|inc|llc|ltd|sarl|bv|company|group)\b/i.test(line));
  const name = lines.map(parseNameLine).find(Boolean) || null;
  const emailDomainCompany = email
    ? email.split('@')[1]?.split('.')[0]?.replace(/[-_]+/g, ' ')
    : '';

  return {
    ...(name || {}),
    email,
    company: companyLine || (emailDomainCompany ? emailDomainCompany.replace(/\b\w/g, c => c.toUpperCase()) : ''),
    phone: phoneMatches[0] || '',
    phoneSecondary: phoneMatches[1] || '',
    language: /(\bdeutsch\b|\bgermany\b|\bdeutschland\b|\bgmbh\b|\bag\b|ä|ö|ü|ß)/i.test(text) ? 'de' : 'en',
    source: 'referral',
  };
}

function parseAIExtraction(raw: string): Partial<LeadFormData> {
  const cleaned = raw.replace(/```json/gi, '').replace(/```/g, '').trim();
  const jsonMatch = cleaned.match(/\{[\s\S]*\}/);
  if (!jsonMatch) return {};
  const parsed = JSON.parse(jsonMatch[0]) as Partial<LeadFormData>;
  return {
    title: cleanExtractedValue(parsed.title),
    firstName: cleanExtractedValue(parsed.firstName),
    lastName: cleanExtractedValue(parsed.lastName),
    email: cleanExtractedValue(parsed.email).toLowerCase(),
    company: cleanExtractedValue(parsed.company),
    phone: cleanExtractedValue(parsed.phone),
    phoneSecondary: cleanExtractedValue(parsed.phoneSecondary),
    language: parsed.language === 'en' ? 'en' : 'de',
    source: 'referral',
  };
}

const AddLeadModal: React.FC<AddLeadModalProps> = ({ isOpen, onClose, onAddLead }) => {
  const { t } = useTranslation('leads');
  const { customSources, addSource } = useCustomLeadSources();
  const formRef = useRef<HTMLFormElement>(null);

  const [formData, setFormData] = useState<LeadFormData>({
    title: '',
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    phoneSecondary: '',
    language: 'de' as Lead['language'],
    source: 'referral' as Lead['source']
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [productId, setProductId] = useState<string>('');
  const [companyId, setCompanyId] = useState<string>('');
  const [showCreateCompany, setShowCreateCompany] = useState(false);
  const [newCompany, setNewCompany] = useState<Partial<Company>>({ name: '', domain: '', industry: '', website: '', employeeCount: '', revenue: '' });
  const [signatureText, setSignatureText] = useState('');
  const [isExtractingContact, setIsExtractingContact] = useState(false);
  const [extractionMessage, setExtractionMessage] = useState<string | null>(null);
  useEscapeKey(onClose, isOpen && !showCreateCompany);
  useEscapeKey(() => setShowCreateCompany(false), isOpen && showCreateCompany);

  useEffect(() => {
    const load = async () => {
      try {
        const orgId = await usersService.getCurrentUserOrgId();
        const list = await companiesService.getCompanies(orgId || undefined);
        setCompanies(list);
        const res = await fetch(`/api/products?orgId=${encodeURIComponent(orgId || '')}`).catch(()=>null);
        if (res && res.ok) {
          const data = await res.json();
          setProducts(data || []);
          const def = (data || []).find((p: Product) => p.isDefault);
          if (def) setProductId(def.id);
        }
      } catch {
        /* Product/company preload failure should not block manual contact entry. */
      }
    };
    if (isOpen) load();
  }, [isOpen]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = t('addLeadModal.errFirstName');
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = t('addLeadModal.errLastName');
    }
    if (!formData.email.trim()) {
      newErrors.email = t('addLeadModal.errEmailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('addLeadModal.errEmailInvalid');
    }
    // Company is now optional
    // if (!formData.company.trim() && !companyId) {
    //   newErrors.company = 'Company is required';
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);

    await new Promise(resolve => setTimeout(resolve, 500));

    let initialScore = 45;
    if (formData.source === 'referral') initialScore += 20;
    if (formData.source === 'website') initialScore += 15;
    if ((formData.email || '').includes('.com')) initialScore += 10;
    if ((formData.company || '').toLowerCase().includes('tech')) initialScore += 15;

    const selectedProduct = products.find(p => p.id === productId);

    const newLead: Omit<Lead, 'id' | 'createdAt' | 'updatedAt' | 'notes' | 'activities'> = {
      title: formData.title.trim() || undefined,
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim().toLowerCase(),
      company: formData.company.trim(),
      companyId: companyId || undefined,
      productId: selectedProduct?.id,
      product: selectedProduct ? { id: selectedProduct.id, key: selectedProduct.key, name: selectedProduct.name, type: selectedProduct.type, leadStages: selectedProduct.leadStages, opportunityStages: selectedProduct.opportunityStages, pricing: selectedProduct.pricing } : undefined,
      value: selectedProduct ? (selectedProduct.pricing?.amount ?? selectedProduct.pricing?.range?.max ?? 0) : undefined,
      phone: formData.phone.trim() || undefined,
      phoneSecondary: formData.phoneSecondary.trim() || undefined,
      language: formData.language || 'en',
      source: formData.source,
      stage: 'untouched',
      score: Math.min(initialScore, 100),
      assignedTo: undefined,
      nextFollowUp: undefined,
      companyData: {
        domain: formData.email.split('@')[1] || '',
        industry: 'Unknown',
        employeeCount: 'Unknown',
        revenue: 'Unknown',
        technologies: [],
        socialProfiles: {},
        description: `Company information for ${formData.company}`,
        keyPersons: [`${formData.firstName} ${formData.lastName}`]
      }
    };

    onAddLead(newLead);
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      title: '',
      firstName: '',
      lastName: '',
      email: '',
      company: '',
      phone: '',
      phoneSecondary: '',
      language: 'de',
      source: 'referral'
    });
    setErrors({});
    setIsSubmitting(false);
    setSignatureText('');
    setExtractionMessage(null);
    onClose();
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const applyExtractedLead = (extracted: Partial<LeadFormData>) => {
    setFormData(prev => ({
      ...prev,
      title: extracted.title || prev.title,
      firstName: extracted.firstName || prev.firstName,
      lastName: extracted.lastName || prev.lastName,
      email: extracted.email || prev.email,
      company: extracted.company || prev.company,
      phone: extracted.phone || prev.phone,
      phoneSecondary: extracted.phoneSecondary || prev.phoneSecondary,
      language: extracted.language || prev.language,
      source: extracted.source || prev.source,
    }));
    setCompanyId('');
    setErrors({});
  };

  const handleExtractContact = async () => {
    const text = signatureText.trim();
    if (!text) {
      setExtractionMessage(t('addLeadModal.msgPasteFirst'));
      return;
    }

    setIsExtractingContact(true);
    setExtractionMessage(null);
    const local = extractLeadLocally(text);

    try {
      let aiExtracted: Partial<LeadFormData> = {};
      if (import.meta.env.VITE_OPENAI_API_KEY) {
        const response = await openaiService.chat([
          {
            role: 'system',
            content: `Extract contact data from an email signature. Return ONLY valid JSON with these keys:
title, firstName, lastName, email, company, phone, phoneSecondary, language.
Use empty strings for missing fields. language must be "de" or "en". Do not invent data.`,
          },
          { role: 'user', content: text },
        ], { model: 'gpt-5-mini', maxTokens: 500 });
        aiExtracted = parseAIExtraction(response);
      }

      applyExtractedLead({ ...local, ...Object.fromEntries(Object.entries(aiExtracted).filter(([, value]) => Boolean(value))) });
      setExtractionMessage(import.meta.env.VITE_OPENAI_API_KEY
        ? t('addLeadModal.msgExtractedAi')
        : t('addLeadModal.msgExtractedLocal'));
    } catch (error) {
      console.warn('AI contact extraction failed, using local parser:', error);
      applyExtractedLead(local);
      setExtractionMessage(t('addLeadModal.msgExtractFailed'));
    } finally {
      setIsExtractingContact(false);
    }
  };

  const handleFieldArrowNavigation = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const target = event.target as HTMLElement;
    if (!target.matches('input, select, textarea')) return;

    const fields = Array.from(formRef.current?.querySelectorAll<HTMLElement>('[data-lead-field]:not([disabled])') || []);
    const index = fields.indexOf(target);
    if (index === -1) return;

    event.preventDefault();
    const nextIndex = event.key === 'ArrowDown'
      ? Math.min(fields.length - 1, index + 1)
      : Math.max(0, index - 1);
    fields[nextIndex]?.focus();
  };

  if (!isOpen) return null;

  return createPortal(
    <>
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={handleClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div role="dialog" aria-modal="true" aria-label={t('addLeadModal.title')} className="relative bg-bg-card backdrop-blur-sm rounded-xl lg:rounded-2xl shadow-heavy w-full max-w-5xl max-h-[95vh] overflow-y-auto transform transition-all">
          <div className="sticky top-0 bg-bg-card backdrop-blur-sm z-10 flex items-center justify-between p-4 sm:p-6 border-b border-subtle">
            <div className="flex items-center space-x-3">
              <IconButton
                variant="ghost"
                size="md"
                icon={<X strokeWidth={1.5} />}
                title={t('addLeadModal.back')}
                aria-label={t('addLeadModal.back')}
                onClick={handleClose}
                className="sm:hidden bg-bg-surface border-0"
              />
              <div className="p-2 bg-info-soft rounded-lg hidden sm:block">
                <User className="w-5 h-5 text-accent" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-txt">{t('addLeadModal.title')}</h3>
                <p className="text-sm text-txt-secondary">{t('addLeadModal.subtitle')}</p>
              </div>
            </div>
            <IconButton
              variant="ghost"
              size="md"
              icon={<X strokeWidth={1.5} />}
              title={t('addLeadModal.close')}
              aria-label={t('addLeadModal.close')}
              onClick={handleClose}
              disabled={isSubmitting}
              className="hidden sm:grid"
            />
          </div>

          <form ref={formRef} onSubmit={handleSubmit} onKeyDown={handleFieldArrowNavigation} className="grid grid-cols-1 gap-4 p-4 sm:p-6 lg:grid-cols-2">
            <div className="rounded-xl border border-default bg-bg-surface p-3 lg:col-span-2">
              <div className="mb-2 flex items-center justify-between gap-2">
                <label className="flex items-center gap-2 text-sm font-semibold text-txt-secondary">
                  <ClipboardPaste className="h-4 w-4 text-accent" />{t('addLeadModal.pasteSignature')}</label>
                <Button
                  type="button"
                  variant="accent"
                  size="sm"
                  icon={isExtractingContact ? <Loader2 className="animate-spin" strokeWidth={1.5} /> : <Wand2 strokeWidth={1.5} />}
                  onClick={handleExtractContact}
                  disabled={isSubmitting || isExtractingContact || !signatureText.trim()}
                >{t('addLeadModal.extract')}</Button>
              </div>
              <textarea
                data-lead-field
                value={signatureText}
                onChange={(e) => setSignatureText(e.target.value)}
                className="min-h-[96px] w-full rounded-xl border border-strong bg-bg-card px-3 py-2 text-sm text-txt focus:border-transparent focus:ring-2 focus:ring-accent/30"
                placeholder={t('addLeadModal.pasteSignaturePlaceholder')}
                disabled={isSubmitting || isExtractingContact}
              />
              {extractionMessage && (
                <div className="mt-2 flex items-start gap-2 text-xs text-txt-secondary">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-warning" />
                  <span>{extractionMessage}</span>
                </div>
              )}
            </div>
            {/* Product */}
            <div>
              <label className="block text-sm font-semibold text-txt-secondary mb-2">{t('addLeadModal.productService')}</label>
              <div className="relative">
                <Package className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-txt-muted" />
                <select data-lead-field value={productId} onChange={(e)=>setProductId(e.target.value)} className="w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-accent/30 appearance-none bg-bg-card">
                  <option value="">{t('addLeadModal.default')}</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-txt-tertiary mt-1">{t('addLeadModal.leadStagesHint')}</p>
            </div>

            {/* Title (optional) */}
            <div>
              <label className="block text-sm font-semibold text-txt-secondary mb-2">
                Title <span className="text-txt-tertiary font-normal">{t('addLeadModal.optional')}</span>
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-txt-muted" />
                <input
                  data-lead-field
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-strong rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-transparent transition-all"
                  placeholder={t('addLeadModal.titlePlaceholder')}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-txt-secondary mb-2">{t('addLeadModal.firstName')}</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-txt-muted" />
                  <input
                    data-lead-field
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-transparent transition-all ${
                      errors.firstName ? 'border-danger bg-danger-soft' : 'border-strong'
                    }`}
                    placeholder={t('addLeadModal.phFirstName')}
                    disabled={isSubmitting}
                  />
                </div>
                {errors.firstName && (
                  <p className="mt-1 text-sm text-danger">{errors.firstName}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-txt-secondary mb-2">{t('addLeadModal.lastName')}</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-txt-muted" />
                  <input
                    data-lead-field
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-transparent transition-all ${
                      errors.lastName ? 'border-danger bg-danger-soft' : 'border-strong'
                    }`}
                    placeholder={t('addLeadModal.phLastName')}
                    disabled={isSubmitting}
                  />
                </div>
                {errors.lastName && (
                  <p className="mt-1 text-sm text-danger">{errors.lastName}</p>
                )}
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-txt-secondary mb-2">{t('addLeadModal.emailAddress')}</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-txt-muted" />
                <input
                  data-lead-field
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-transparent transition-all ${
                    errors.email ? 'border-danger bg-danger-soft' : 'border-strong'
                  }`}
                  placeholder={t('addLeadModal.phEmail')}
                  disabled={isSubmitting}
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-danger">{errors.email}</p>
              )}
            </div>

            {/* Company */}
            <div>
              <label className="block text-sm font-semibold text-txt-secondary mb-2">
                Company <span className="text-txt-tertiary font-normal">{t('addLeadModal.optional')}</span>
              </label>
              <div className="flex items-center space-x-2">
                <div className="relative flex-1">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-txt-muted" />
                  <select
                    data-lead-field
                    value={companyId}
                    onChange={(e) => {
                      const val = e.target.value;
                      setCompanyId(val);
                      const selected = companies.find(c => c.id === val);
                      if (selected) {
                        setFormData(prev => ({ ...prev, company: selected.name }));
                      }
                    }}
                    className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-transparent transition-all appearance-none bg-bg-card ${
                      errors.company ? 'border-danger bg-danger-soft' : 'border-strong'
                    }`}
                    disabled={isSubmitting}
                  >
                    <option value="">{t('addLeadModal.selectCompany')}</option>
                    {companies.map(c => (
                      <option key={c.id} value={c.id}>{c.name} ({c.industry})</option>
                    ))}
                  </select>
                </div>
                <Button
                  type="button"
                  variant="accent"
                  className="!p-3 flex-shrink-0 min-w-0"
                  icon={<Plus strokeWidth={1.5} />}
                  title={t('addLeadModal.createNewCompany')}
                  onClick={() => setShowCreateCompany(true)}
                  disabled={isSubmitting}
                  aria-label={t('addLeadModal.createNewCompany')}
                />
              </div>
              {errors.company && (
                <p className="mt-1 text-sm text-danger">{errors.company}</p>
              )}
            </div>

            {/* Phone (Optional) */}
            <div>
              <label className="block text-sm font-semibold text-txt-secondary mb-2">{t('addLeadModal.phoneNumber')}</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-txt-muted" />
                <input
                  data-lead-field
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-strong rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-transparent transition-all"
                  placeholder="+1 (555) 123-4567"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-txt-secondary mb-2">
                Secondary phone <span className="text-txt-tertiary font-normal">{t('addLeadModal.optional')}</span>
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-txt-muted" />
                <input
                  data-lead-field
                  type="tel"
                  value={formData.phoneSecondary}
                  onChange={(e) => handleInputChange('phoneSecondary', e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-strong rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-transparent transition-all"
                  placeholder={t('addLeadModal.mobilePlaceholder')}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Language */}
            <div>
              <label className="block text-sm font-semibold text-txt-secondary mb-2">{t('addLeadModal.communicationLanguage')}</label>
              <div className="relative">
                <Languages className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-txt-muted" />
                <select
                  data-lead-field
                  value={formData.language || 'en'}
                  onChange={(e) => handleInputChange('language', e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-strong rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-transparent transition-all appearance-none bg-bg-card"
                  disabled={isSubmitting}
                >
                  {LANGUAGES.map(lang => (
                    <option key={lang.code} value={lang.code}>
                      {lang.flag} {lang.name} ({lang.nativeName})
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-txt-tertiary mt-1">{t('addLeadModal.languageHint')}</p>
            </div>

            {/* Source */}
            <div>
              <label className="block text-sm font-semibold text-txt-secondary mb-2">{t('addLeadModal.leadSource')}</label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-txt-muted" />
                <select
                  data-lead-field
                  value={formData.source}
                  onChange={async (e) => {
                    const val = e.target.value;
                    if (val === '__add_source__') {
                      const added = await addSource(window.prompt(t('newSourcePrompt')) || '');
                      if (added) handleInputChange('source', added);
                      return;
                    }
                    handleInputChange('source', val);
                  }}
                  className="w-full pl-10 pr-4 py-3 border border-strong rounded-xl focus:ring-2 focus:ring-accent/30 focus:border-transparent transition-all appearance-none bg-bg-card"
                  disabled={isSubmitting}
                >
                  <option value="website">{t('addLeadModal.srcWebsite')}</option>
                  <option value="referral">{t('addLeadModal.srcReferral')}</option>
                  <option value="cold-outreach">{t('addLeadModal.srcColdOutreach')}</option>
                  <option value="social">{t('addLeadModal.srcSocialMedia')}</option>
                  <option value="partner">{t('addLeadModal.srcPartner')}</option>
                  {customSources.map(s => <option key={s} value={s}>{s}</option>)}
                  <option value="__add_source__">＋ {t('addSource')}</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 pt-4 lg:col-span-2">
              <Button
                type="button"
                variant="ghost"
                fullWidth
                onClick={handleClose}
                disabled={isSubmitting}
              >{t('addLeadModal.cancel')}</Button>
              <Button
                type="submit"
                variant="primary"
                fullWidth
                loading={isSubmitting}
                icon={!isSubmitting ? <Save strokeWidth={1.5} /> : undefined}
              >
                {isSubmitting ? 'Adding...' : 'Add Lead'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
    {/* existing Create Company modal retained below */}
    {showCreateCompany && (
      <div className="fixed inset-0 z-[60] overflow-y-auto">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowCreateCompany(false)} />
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="relative bg-bg-card rounded-2xl shadow-heavy w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-default flex items-center justify-between">
              <h3 className="text-lg font-bold text-txt">{t('addLeadModal.createCompany')}</h3>
              <IconButton
                variant="ghost"
                size="md"
                icon={<X strokeWidth={1.5} />}
                title={t('addLeadModal.close')}
                aria-label={t('addLeadModal.close')}
                onClick={() => setShowCreateCompany(false)}
              />
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-1">{t('addLeadModal.fieldName')}</label>
                <input value={newCompany.name || ''} onChange={e => setNewCompany({ ...newCompany, name: e.target.value })} className="w-full border border-strong rounded-lg px-3 py-2 focus:ring-2 focus:ring-accent/30" />
              </div>
              <div>
                <label className="block text-sm font-medium text-txt-secondary mb-1">{t('addLeadModal.fieldDomain')}</label>
                <input value={newCompany.domain || ''} onChange={e => setNewCompany({ ...newCompany, domain: e.target.value.toLowerCase().trim() })} className="w-full border border-strong rounded-lg px-3 py-2 focus:ring-2 focus:ring-accent/30" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-txt-secondary mb-1">{t('addLeadModal.fieldIndustry')}</label>
                  <input value={newCompany.industry || ''} onChange={e => setNewCompany({ ...newCompany, industry: e.target.value })} className="w-full border border-strong rounded-lg px-3 py-2 focus:ring-2 focus:ring-accent/30" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-txt-secondary mb-1">{t('addLeadModal.srcWebsite')}</label>
                  <input value={newCompany.website || ''} onChange={e => setNewCompany({ ...newCompany, website: e.target.value })} className="w-full border border-strong rounded-lg px-3 py-2 focus:ring-2 focus:ring-accent/30" />
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-default flex flex-wrap gap-2 justify-end">
              <Button type="button" variant="ghost" size="sm" onClick={() => setShowCreateCompany(false)}>{t('addLeadModal.cancel')}</Button>
              <Button
                type="button"
                variant="accent"
                size="sm"
                onClick={async () => {
                if (!newCompany.name) return;
                const orgId = await usersService.getCurrentUserOrgId();
                if ((newCompany.domain || '').trim()) {
                  const existing = await companiesService.findByDomain((newCompany.domain as string).toLowerCase().trim(), orgId || undefined);
                  if (existing) {
                    setCompanyId(existing.id);
                    setFormData(prev => ({ ...prev, company: existing.name }));
                    setShowCreateCompany(false);
                    return;
                  }
                }
                const companyPayload: Omit<Company, 'id' | 'createdAt' | 'updatedAt'> & { orgId?: string } = {
                  name: newCompany.name!,
                  domain: newCompany.domain || '',
                  industry: newCompany.industry || '',
                  employeeCount: newCompany.employeeCount || '',
                  revenue: newCompany.revenue || '',
                  description: '',
                  website: newCompany.website || '',
                  socialProfiles: {},
                  orgId: orgId || undefined,
                };
                const id = await companiesService.createCompany(companyPayload);
                const refreshed = await companiesService.getCompanies(orgId || undefined);
                setCompanies(refreshed);
                setCompanyId(id);
                const created = refreshed.find(c => c.id === id);
                if (created) {
                  setFormData(prev => ({ ...prev, company: created.name }));
                }
                setShowCreateCompany(false);
              }}
              >{t('addLeadModal.create')}</Button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  , document.body);
};

export default AddLeadModal;
